import React from "react";
import Dialog from "components/dialog";
import { theme } from "styles/theme";
import Button from "components/form/button";
import Checkbox from "components/form/checkbox";
import { format } from "lib/globalize-util";
import _ from "lodash";
import { $, merge } from "glamor";
import type { IScope } from "reducers/scope";
import { FormatMessage } from "react-globalize";
import { IOrder } from "records/order";
import { DateLabel } from "./date-checkbox";
import * as PropTypes from "prop-types";
import { BuildOrderActions } from "constants/analytics";
import RadioButton from "material-ui/RadioButton/RadioButton";
import RadioButtonGroup from "material-ui/RadioButton/RadioButtonGroup";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { buildOrderDictionary as boDictionary, formatDateKey } from "../build-order-utils";
import { DATATEST } from "../../../constants/integration";

enum ActionType {
    Copy = "copy",
    Move = "move",
}

interface Product {
    _id: string;
    name: string;
    imagesT: string[];
}

interface Item {
    product: string;
}

type ProductGroup = { key: string; displayName: string; items: Item[] };

export interface IBulkDateOrganizer {
    groupedProducts: ProductGroup[];
    selectedGroup: ProductGroup;
    index: number;
    products: Record<string, Product>;
}

interface IBulkDateOrganizerProps {
    bulkDateOrganizer: IBulkDateOrganizer;
    onClose: () => void;
    scope: IScope;
    order: IOrder;
}

const PRODUCT_PREVIEW_COUNT = 25;
const placeholderURL = "//cdn1.nuorder.com/brands/1womenswholesale_missing.jpg";
export const getFallbackImageUrl = (scope: IScope) =>
    `//cdn1.nuorder.com/brands/${_.get(scope.brand, "settings.missing_logo_image")}.jpg`;

export const PreviewItem: React.FC<{ imageUrl: string; tooltip: string }> = ({ imageUrl, tooltip }) => (
    <li title={tooltip}>
        <img src={imageUrl} alt={tooltip} />
    </li>
);

export const BulkDateOrganizer: React.FunctionComponent<IBulkDateOrganizerProps> = (
    {
        // long live the prettier!
        bulkDateOrganizer,
        onClose,
        scope,
        order,
    },
    { app }
) => {
    const styles = React.useMemo(() => {
        return {
            base: merge(
                $(" .headerDetails", {
                    display: "flex",
                    fontSize: "12px",
                    paddingBottom: "10px",
                }),
                $(" .productPreview", {
                    display: "flex",
                    flex: 1,
                    listStyleType: "none",
                    padding: 0,
                    margin: 0,
                    height: "86px",
                    overflow: "auto",
                    overflowY: "hidden",
                    gap: "4px",
                    flexWrap: "nowrap",
                }),
                $(" .productPreview:after", {
                    content: "''",
                    display: "block",
                    flexGrow: 10,
                }),
                $(" .productPreview > li", {
                    flex: "1 0",
                }),
                $(" .productPreview > li > img", {
                    maxHeight: "100%",
                    minWidth: "100%",
                    objectFit: "cover",
                    verticalAlign: "bottom",
                }),
                $(" .deliveryDetails", {
                    textAlign: "right",
                    flexDirection: "column",
                    display: "flex",
                    flex: 1,
                }),
                $(" .daysLength", {
                    fontWeight: "bold",
                }),
                $(" .fieldsSection", {
                    paddingTop: "10px",
                    borderTop: `1px solid ${theme.palette.lightGrey}`,
                }),
                $(" .deliveriesPill", {
                    backgroundColor: theme.palette.accent1Color,
                    color: theme.palette.canvasColor,
                    padding: "2px 10px",
                    borderRadius: "15px",
                    lineHeight: "30px",
                }),
                $(" .availabilityDates .fieldValue", {
                    fontWeight: "bold",
                    paddingLeft: "5px",
                }),
                $(" .numDeliveries", {
                    fontWeight: "bold",
                    height: "100%",
                }),
                $(" .datesCopy", {
                    fontSize: "12px",
                    paddingBottom: "10px",
                }),
                $(" .productCount", {
                    fontSize: "11px",
                    color: theme.palette.grey,
                    paddingLeft: "5px",
                }),
                $(" .nullDates", {
                    display: "flex",
                    width: "68%",
                    height: "88px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: theme.palette.lightestGrey,
                    marginBottom: "10px",
                })
            ),
            actions: merge(
                $(" .save", {
                    marginLeft: "10px !important",
                })
            ),
        };
    }, []);

    const [shipDatesAction, setShipDatesAction] = React.useState<ActionType>(ActionType.Copy);
    const [selectedShipDate, setSelectedShipDate] = React.useState<string>();
    const [ignoreUnits, setIgnoreUnits] = React.useState<boolean>(false);
    const buildOrderDictionary = React.useMemo(
        () => boDictionary(order.__build_order?.ship_dates),
        [order.__build_order?.ship_dates]
    );

    const productItems = React.useMemo(
        () =>
            _(bulkDateOrganizer.selectedGroup.items)
                .map("product")
                .uniq()
                .take(PRODUCT_PREVIEW_COUNT)
                .map((_id) => {
                    const product = bulkDateOrganizer.products[_id];
                    return {
                        _id,
                        name: product.name || "",
                        imageUrl: product?.imagesT?.[0] || getFallbackImageUrl(scope) || placeholderURL,
                    };
                })
                .value(),
        [scope, bulkDateOrganizer.products, bulkDateOrganizer.selectedGroup.items]
    );

    React.useEffect(() => {
        global.analytics.track(BuildOrderActions.WODateOrganizerBulkShipmentSelected);
    }, []);

    const handleUpdateCart = React.useCallback(() => {
        app.legacy.sendMessage({
            action: "bulk-date-organizer-actions",
            index: bulkDateOrganizer.index,
            selectedGroup: bulkDateOrganizer.selectedGroup,
            selectedShipDate,
            shipDatesAction,
            ignoreUnits,
        });
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedShipDate, shipDatesAction, ignoreUnits, bulkDateOrganizer?.selectedGroup, bulkDateOrganizer?.index]);

    const nonBuyerSelectedDeliveries = React.useMemo(() => {
        const _nonBuyerSelectedDeliveries = [];
        bulkDateOrganizer.groupedProducts.forEach((group) => {
            const value = group.key.split("_");
            const key = formatDateKey(value[0], value[1]);
            if (!buildOrderDictionary[key] && group.key !== bulkDateOrganizer.selectedGroup.key) {
                _nonBuyerSelectedDeliveries.push(
                    <RadioButton
                        key={key}
                        value={key}
                        label={<DateLabel shipStart={value[0]} shipEnd={value[1]} productCount={group.items.length} />}
                    />
                );
            }
        });
        return _nonBuyerSelectedDeliveries;
    }, [buildOrderDictionary, bulkDateOrganizer]);

    const buyerSelectedDeliveries = React.useMemo(() => {
        const _buyerSelectedDeliveries = [];
        order.__build_order?.ship_dates?.forEach((shipDate) => {
            const shipDatesKey = formatDateKey(shipDate.ship_start, shipDate.ship_end);
            const bulkDatesOrganizerDates = bulkDateOrganizer.selectedGroup.key.split("_");
            const bulkDatesOrganizerKey = formatDateKey(bulkDatesOrganizerDates[0], bulkDatesOrganizerDates[1]);
            if (bulkDatesOrganizerKey !== shipDatesKey) {
                _buyerSelectedDeliveries.push(
                    <RadioButton
                        key={shipDatesKey}
                        value={shipDatesKey}
                        label={
                            <DateLabel
                                shipStart={shipDate.ship_start}
                                shipEnd={shipDate.ship_end}
                                productCount={shipDate.line_items?.length}
                            />
                        }
                    />
                );
            }
        });
        return _buyerSelectedDeliveries;
    }, [order, bulkDateOrganizer]);

    const handleChangeActionType = React.useCallback((_e: React.FormEvent<{}>, actionType: ActionType) => {
        global.analytics.track(BuildOrderActions.WODateOrganizerBulkShipmentAction, { selected: actionType });
        if (actionType === ActionType.Move) {
            setIgnoreUnits(false);
        }
        setShipDatesAction(actionType);
    }, []);

    const handleChangeIgnoreUnits = React.useCallback((_e: React.FormEvent<{}>, checked: boolean) => {
        global.analytics.track(BuildOrderActions.WODateOrganizerBulkShipmentIgnoreUnits, { checked });
        setIgnoreUnits(checked);
    }, []);

    const handleChangeSelectedShipDate = React.useCallback((e, selected: string) => {
        const isNonBuyer = e.target.getAttribute("data-value") === "nonBuyerSelected";
        if (isNonBuyer) {
            global.analytics.track(BuildOrderActions.WODateOrganizerBulkShipmentDuplicatedDates);
        } else {
            global.analytics.track(BuildOrderActions.WODateOrganizerBulkShipmentDuplicatedDates);
        }
        setSelectedShipDate(selected);
    }, []);

    return (
        <React.Fragment>
            <Dialog
                title={`${format("Bulk edit shipment")}: ${bulkDateOrganizer.selectedGroup.displayName}`}
                modal={false}
                repositionOnUpdate={false}
                autoScrollBodyContent={false}
                open={!!bulkDateOrganizer}
                onRequestClose={onClose}
                contentClassName={`${styles.base}`}
                style={{ zIndex: 1499 }}
                actionsContainerClassName={`${styles.actions}`}
                actionsContainerStyle={{ textAlign: "left" }}
                actions={
                    <FormGroup row style={{ width: "100%" }}>
                        <FormControlLabel
                            label={format("Ignore units")}
                            style={{ margin: "0 auto 0 0", whiteSpace: "nowrap" }}
                            control={
                                <Checkbox
                                    checked={ignoreUnits}
                                    disabled={shipDatesAction === ActionType.Move}
                                    onCheck={handleChangeIgnoreUnits}
                                    data-test={DATATEST.BUILD_ORDER.IGNORE_UNITS}
                                />
                            }
                        />
                        <Button label={format("Cancel")} onClick={onClose} data-test={DATATEST.CANCEL_BTN} />
                        <Button
                            className="save"
                            label={format("Apply")}
                            primary={true}
                            disabled={!selectedShipDate}
                            onClick={handleUpdateCart}
                            data-test={DATATEST.SAVE_BTN}
                        />
                    </FormGroup>
                }
                dataTest={{ container: DATATEST.BUILD_ORDER.BULK_ORDER_ORGANIZER }}
            >
                <div className="headerDetails">
                    <ul className="productPreview" data-test={DATATEST.BUILD_ORDER.PRODUCT_PREVIEW}>
                        {productItems.map(({ _id, imageUrl, name }) => (
                            <PreviewItem key={_id} imageUrl={imageUrl} tooltip={name} />
                        ))}
                    </ul>
                    <div className="deliveryDetails">
                        <FormatMessage
                            className="numDeliveries"
                            path="Products in this delivery [span]|numProducts|[span]"
                            variables={{ numProducts: bulkDateOrganizer.selectedGroup.items.length }}
                            elements={{ span: <span className="deliveriesPill" /> }}
                        />
                        <FormatMessage
                            className="windowLength"
                            path="|brand| has [span]|windowLength| day[span] ship window"
                            variables={{
                                brand: scope.brand?.name,
                                windowLength: scope.brand?.getPreference?.("default_delivery_window"),
                            }}
                            elements={{ span: <span className="daysLength" /> }}
                        />
                    </div>
                </div>
                <div className="fieldsSection">
                    <RadioButtonGroup
                        name="action-type"
                        valueSelected={shipDatesAction}
                        onChange={handleChangeActionType} /* style={{ width: "180px" }} */
                    >
                        <RadioButton
                            value={ActionType.Copy}
                            label={format("Copy")}
                            data-test={DATATEST.BUILD_ORDER.OPERATION_COPY}
                        />
                        <RadioButton
                            value={ActionType.Move}
                            label={format("Move")}
                            data-test={DATATEST.BUILD_ORDER.OPERATION_MOVE}
                        />
                    </RadioButtonGroup>
                </div>
                <div className="fieldsSection" data-test={DATATEST.BUILD_ORDER.BUYER_SELECTED_DELIVERIES}>
                    <div className="datesCopy">
                        {shipDatesAction === ActionType.Copy ? (
                            <FormatMessage
                                path="All products and units from |dates| will remain in this shipment and will be copied to the selected shipment."
                                variables={{ dates: bulkDateOrganizer.selectedGroup.displayName }}
                            />
                        ) : (
                            <FormatMessage
                                path="All products and units in |dates| will be moved from this shipment to the selected shipment."
                                variables={{ dates: bulkDateOrganizer.selectedGroup.displayName }}
                            />
                        )}
                    </div>
                    {buyerSelectedDeliveries.length ? (
                        <RadioButtonGroup
                            name="order-dates"
                            data-value="nonBuyerSelected"
                            valueSelected={selectedShipDate}
                            onChange={handleChangeSelectedShipDate}
                        >
                            {buyerSelectedDeliveries}
                        </RadioButtonGroup>
                    ) : (
                        <div className="nullDates">{format("No ship dates present.")}</div>
                    )}
                </div>
                {nonBuyerSelectedDeliveries.length > 0 && (
                    <div className="fieldsSection" data-test={DATATEST.BUILD_ORDER.NON_BUYER_SELECTED_DELIVERIES}>
                        <div className="datesCopy">
                            {format(
                                "There are products in your cart that fall outside your selected ship dates. The dates below are based on these products."
                            )}
                        </div>
                        <RadioButtonGroup
                            name="order-dates"
                            valueSelected={selectedShipDate}
                            onChange={handleChangeSelectedShipDate}
                        >
                            {nonBuyerSelectedDeliveries}
                        </RadioButtonGroup>
                    </div>
                )}
            </Dialog>
        </React.Fragment>
    );
};

BulkDateOrganizer.contextTypes = { app: PropTypes.object.isRequired };
